import { Button, TextField } from "@simplybusiness/mobius";
import type { ChangeEvent } from "react";
import { QUESTIONS_WITH_TOGGLE } from "../../../../../../../constants/constants";
import type { QuestionComponentProps } from "../../types";
import { useQuestionRefs } from "../../../../../../../hooks/useQuestionRefs";

type TextFieldType = (typeof TEXT_FIELD_TYPES)[number];

const TEXT_FIELD_TYPES = [
  "text",
  "email",
  "tel",
  "number",
  "postcode",
] as const;
const DEFAULT_TYPE = "text";

const isValidFieldType = (type: string) =>
  TEXT_FIELD_TYPES.includes(type as TextFieldType);

export const TextQuestion = ({
  questionFieldProps,
  type = DEFAULT_TYPE,
  addressModes,
  setAddressMode,
  associatedQuestionName,
}: QuestionComponentProps) => {
  const {
    value,
    onChange,
    readOnly,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { id, name } = questionFieldProps;
  const inputType = isValidFieldType(type) ? type : DEFAULT_TYPE;
  const { focusQuestion, registerButtonRef, getButtonRef } = useQuestionRefs();

  // Function to handle button ref and update button store
  const handleButtonRef = (buttonElement: HTMLButtonElement | null) => {
    if (buttonElement && QUESTIONS_WITH_TOGGLE.includes(id!)) {
      registerButtonRef(
        `address-lookup-${associatedQuestionName}`,
        buttonElement,
      );
    }
  };

  // Handle address mode
  const addressMode = associatedQuestionName
    ? addressModes?.[associatedQuestionName]
    : undefined;
  const isAddressQuestion = !!addressMode;

  if (isAddressQuestion && addressMode === "lookup") {
    return null;
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onChange?.({
      target: { name, value: { [id!]: value, [name]: value } },
    });
  };

  const handleBlur = (event: React.FocusEvent) => {
    // Check if the blur was caused by clicking the address lookup button
    const relatedTarget = event.relatedTarget as HTMLElement;
    const addressLookupButton = getButtonRef(
      `address-lookup-${associatedQuestionName}`,
    );
    const wasAddressLookupButtonClicked =
      relatedTarget &&
      addressLookupButton &&
      relatedTarget === addressLookupButton;

    if (wasAddressLookupButtonClicked) {
      return;
    }

    questionFieldProps.onBlur?.(event);
  };

  return (
    <>
      <TextField
        {...otherProps}
        type={inputType}
        value={(value ?? "") as string}
        onChange={handleChange}
        onBlur={handleBlur}
        isReadOnly={!!readOnly}
      />
      {QUESTIONS_WITH_TOGGLE.includes(id!) && !!associatedQuestionName && (
        <Button
          className="mobius-text-question__button-link"
          size="sm"
          variant="link"
          ref={handleButtonRef}
          onClick={() => {
            setAddressMode?.(associatedQuestionName, "lookup");
            setTimeout(() => {
              focusQuestion(associatedQuestionName);
            }, 0);
          }}
        >
          Use address lookup
        </Button>
      )}
    </>
  );
};
